import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useTopic, useTopicSession, useTopicDocuments, useTopicNotes, useCreateStudyNote, StudyNote, LessonSlide } from "@/hooks/useQueryHooks";
import { useTopicVideos, useAddTopicVideo, useDeleteTopicVideo, TopicVideo } from "@/hooks/useTopicVideos";
import { useTopicLibraryItems, LibraryItem } from "@/hooks/useTopicLibraryItems";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import AppLayout from "@/components/layout/AppLayout";
import ChatTabs from "./ChatTabs";
import ChatThread from "./ChatThread";
import { useIsTabletOrMobile } from "@/hooks/use-tablet-mobile";
import AgentDock from "./AgentDock";
import { StudyNoteEditor, StudyNoteEditorRef } from "@/components/notes/StudyNoteEditor";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, FileText, ChevronDown } from "lucide-react";
import CanvasSection from "./CanvasSection";
import { DragDropUpload } from "@/components/document/DragDropUpload";
import { YouTubeUrlModal } from "@/components/youtube/YouTubeUrlModal";
// Legacy GeminiLive removed; using Multimodal Live via adapter unconditionally
import { useLiveAdapter } from "@/hooks/useLiveAdapter";
import { useConversationManager } from "@/hooks/useConversationManager";
import { useStudyTimeTracker } from "@/hooks/useStudyTimeTracker";
import { config } from "@/lib/config";
import { triggerYouTubeAnalysis } from "@/lib/youtube-analysis-service";
const ChatPage = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'chat' | 'notebook' | 'canvas'>('chat');

  // Check if we should open modals from URL params
  const params = new URLSearchParams(location.search);
  const shouldOpenUploadModal = params.get('openUploadModal') === 'true';
  const shouldOpenYouTubeModal = params.get('openYouTubeModal') === 'true';
  const isTabletOrMobile = useIsTabletOrMobile();

  // Selected document for context
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);

  // New state for separate chat and notebook columns
  const [isChatColumnVisible, setIsChatColumnVisible] = useState(false);
  const [isNotebookColumnVisible, setIsNotebookColumnVisible] = useState(false);
  const [chatColumnWidth, setChatColumnWidth] = useState(20); // percentage - 20% of viewport width
  const [notebookColumnWidth, setNotebookColumnWidth] = useState(25); // percentage - 25% of viewport width

  // Canvas integration state
  const [canvasDocumentOpenFn, setCanvasDocumentOpenFn] = useState<((documentId: string, documentName: string) => void) | null>(null);
  const [canvasVideoOpenFn, setCanvasVideoOpenFn] = useState<((video: TopicVideo) => void) | null>(null);
  const [canvasNoteOpenFn, setCanvasNoteOpenFn] = useState<((note: StudyNote) => void) | null>(null);
  const [canvasUploadDocumentFn, setCanvasUploadDocumentFn] = useState<((file: File) => Promise<void>) | null>(null);
  const [canvasAddYouTubeVideoFn, setCanvasAddYouTubeVideoFn] = useState<((videoId: string, title: string, embedUrl: string) => void) | null>(null);
  const [canvasOpenImagesWithAutoSaveFn, setCanvasOpenImagesWithAutoSaveFn] = useState<((images: Array<{ id: string; file: File; blobUrl: string; name: string; }>, shouldSave?: boolean) => void) | null>(null);

  // Vision integration state (for frame capture and context sharing)
  const [getCanvasEl, setGetCanvasEl] = useState<(() => HTMLElement | null) | null>(null);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [getYouTubePlayer, setGetYouTubePlayer] = useState<(() => import('@/components/youtube/YouTubeEmbed').YouTubePlayerRef | null) | null>(null);
  const [currentYouTubeVideoId, setCurrentYouTubeVideoId] = useState<string | null>(null);
  const [activeSlideContext, setActiveSlideContext] = useState<{
    slide: LessonSlide | null;
    lessonTitle?: string;
    progress?: {
      total: number;
      completed: number;
      pendingTitles: string[];
    };
  }>({
    slide: null
  });

  // Canvas visibility state
  const [hasCanvasContent, setHasCanvasContent] = useState(false);

  // Column resize state (kept for backward compatibility but repurposed)
  const [column1Width, setColumn1Width] = useState(30); // percentage
  const [isResizing, setIsResizing] = useState(false);

  // Input mode state
  const [inputMode, setInputMode] = useState<'talk' | 'chat'>('talk');
  const [chatInputText, setChatInputText] = useState('');

  // Screen share state
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  // Document upload state
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Pending upload state for deferred topic creation flow
  const [pendingUploadFile, setPendingUploadFile] = useState<{
    file: File;
    topicId: string;
  } | null>(null);

  // Pending YouTube video state for deferred topic creation flow
  const [pendingYouTubeVideo, setPendingYouTubeVideo] = useState<{
    videoId: string;
    title: string;
    embedUrl: string;
    topicId: string;
  } | null>(null);

  // Pending images state for deferred topic creation flow
  const [pendingImages, setPendingImages] = useState<{
    images: Array<{ id: string; file: File; blobUrl: string; name: string; }>;
    topicId: string;
    shouldAutoSave: boolean;
  } | null>(null);

  // Ref to prevent concurrent uploads
  const uploadInProgressRef = useRef(false);

  // YouTube video state
  const [showYouTubeModal, setShowYouTubeModal] = useState(false);

  // Document delete dialog state - similar to legacy Topic2Page.tsx:237-240
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeletingDoc, setIsDeletingDoc] = useState(false);

  // Video delete dialog state - similar to legacy Topic2Page.tsx:242-245
  const [deleteVideoOpen, setDeleteVideoOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeletingVideo, setIsDeletingVideo] = useState(false);

  // Use query hooks for data fetching
  const {
    data: topic,
    isLoading: loading,
    error: topicError
  } = useTopic(id);
  const {
    data: session,
    isLoading: sessionLoading
  } = useTopicSession(id);
  const {
    data: libraryItems = [],
    isLoading: libraryLoading
  } = useTopicLibraryItems(id);
  const {
    data: notes = [],
    isLoading: notesLoading
  } = useTopicNotes(id);
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const [userProfile, setUserProfile] = useState<{
    full_name?: string | null;
  }>({});

  // Separate documents and videos from combined library items for backward compatibility
  const documents = useMemo(() => libraryItems.filter(item => item.type === 'document').map(item => ({
    id: item.id,
    name: item.name || item.title,
    uploadDate: item.uploadDate || '',
    size: item.size || '',
    summary: item.summary,
    status: item.status || 'processed',
    thumbnail_url: item.thumbnail_url
  })), [libraryItems]);
  const videos = useMemo(() => libraryItems.filter(item => item.type === 'video').map(item => ({
    id: item.id,
    topic_id: id!,
    user_id: user?.id || '',
    video_id: item.video_id || '',
    title: item.title,
    channel_title: item.channel_title,
    description: item.description,
    duration: item.duration,
    embed_url: item.embed_url || '',
    thumbnail_url: item.thumbnail_url,
    transcript: item.transcript,
    chapters: item.chapters,
    has_chapters: item.has_chapters || false,
    full_text: item.full_text,
    language: item.language || 'en',
    total_segments: item.total_segments || 0,
    video_info: item.video_info,
    created_at: item.created_at,
    updated_at: item.created_at // Fallback to created_at if updated_at is not available
  }) satisfies TopicVideo), [libraryItems, id, user?.id]);

  // Simplified conversation management - one session per topic
  const conversationManager = useConversationManager(session, user, topic, id);

  // Extract conversation manager interface
  const {
    // State
    messages,
    setMessages,
    unsavedMessageCount,
    setUnsavedMessageCount,
    combinedMessages,
    currentSessionStartTime,
    accumulatedDurationSeconds,
    // Core Functions
    formatTimeAgo,
    loadConversationHistory,
    saveCurrentConversation,
    persistConversationBatch,
    // Session Management API
    appendMessage,
    startCurrentSession,
    endCurrentSession,
    scheduleAutoTitleIfEligible
  } = conversationManager;

  // Initialize comprehensive study time tracking
  const studyTimeTracker = useStudyTimeTracker({
    topicId: id!,
    sessionId: session?.id || null,
    // Direct session ID
    onTimeUpdate: totalSeconds => {
      console.log(`📚 Total study time: ${totalSeconds}s`);
    }
  });
  const messageSeqRef = useRef(0);
  const previousTopicIdRef = useRef<string | undefined>(id);

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      try {
        const {
          data: profile,
          error
        } = await supabase.from('profiles').select('full_name').eq('id', user.id).maybeSingle();
        if (!error && profile) {
          setUserProfile({
            full_name: profile.full_name
          });
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchUserProfile();
  }, [user]);

  // Convert Message[] to TranscriptMessage[] for ChatThread compatibility
  const convertToTranscriptMessages = useCallback((messages: Array<{
    id: string;
    type: 'user' | 'agent';
    content: string;
    timestamp: Date | string;
    pdf_page?: number;
    // Allow passthrough fields without narrowing away at runtime
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } & Record<string, any>>) => {
    return messages.map(msg => ({
      ...msg,
      // Ensure UI sees the streaming flag if present on live messages
      streaming: (msg as any).streaming ?? false,
      type: msg.type === 'agent' ? 'ai' as const : msg.type,
      // Convert 'agent' to 'ai'
      timestamp: typeof msg.timestamp === 'string' ? msg.timestamp : msg.timestamp?.toISOString?.() || new Date().toISOString(),
      pdf_page: msg.pdf_page || 0
    }));
  }, []);

  // Convert combined messages for display in ChatThread
  const transcriptMessages = useMemo(() => {
    return convertToTranscriptMessages(combinedMessages || []);
  }, [combinedMessages, convertToTranscriptMessages]);

  // Video mutations for saving and deleting
  const addTopicVideoMutation = useAddTopicVideo();
  const deleteVideoMutation = useDeleteTopicVideo();

  // Note management state
  const [activeNote, setActiveNote] = useState<StudyNote | null>(null);
  const createNoteMutation = useCreateStudyNote();
  const editorRef = useRef<StudyNoteEditorRef | null>(null);

  // Agent/Audio integration - direct session ID (no more activeSession complexity)
  const geminiLive = useLiveAdapter({
    selectedDocument,
    activeSessionId: session?.id || null,
    // Direct session ID - always available
    topicId: id,
    setMessages,
    setUnsavedMessageCount,
    messageSeqRef,
    getCanvasEl,
    screenStream,
    getYouTubePlayer,
    currentYouTubeVideoId,
    currentSlideContext: activeSlideContext,
    startCurrentSession,
    endCurrentSession,
    appendMessage,
    onAgentActivityChange: isActive => {
      if (isActive) {
        studyTimeTracker.startActivity('agent');
      } else {
        studyTimeTracker.endCurrentActivity();
        if (session?.id) {
          studyTimeTracker.startActivity('general');
        }
      }
    }
  });

  // Calculate user speaking state for VAD feedback (like legacy implementation)
  const isUserSpeaking = !geminiLive.isUserMuted && geminiLive.isAgentActive && geminiLive.isUserSpeakingVad;

  // Handle URL parameters cleanup for auto-opening modals
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenUploadModal = params.get('openUploadModal') === 'true';
    const shouldOpenYouTubeModal = params.get('openYouTubeModal') === 'true';
    if (shouldOpenUploadModal || shouldOpenYouTubeModal) {
      // Clear the URL parameters immediately since we're opening the modal via prop
      const newParams = new URLSearchParams(location.search);
      newParams.delete('openUploadModal');
      newParams.delete('openYouTubeModal');
      const newUrl = `${location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search, location.pathname]);

  // Handle pending upload from deferred topic creation flow - Step 1: Detect and store
  useEffect(() => {
    const pendingUpload = location.state?.pendingUpload;

    if (pendingUpload?.file && pendingUpload?.topicId && pendingUpload?.shouldOpenOptimistically) {
      console.log('📤 Detected pending upload from router state');
      setPendingUploadFile({
        file: pendingUpload.file,
        topicId: pendingUpload.topicId
      });

      // Clear router state immediately
      navigate(location.pathname + location.search, {
        replace: true,
        state: {}
      });
    }
  }, [location.state, navigate, location.pathname, location.search]);

  // Handle pending upload from deferred topic creation flow - Step 2: Execute when canvas function is ready
  useEffect(() => {
    // Only proceed if we have pending upload, the function is available, AND no upload is in progress
    if (pendingUploadFile && canvasUploadDocumentFn && !uploadInProgressRef.current) {
      console.log('📤 Canvas function available, triggering optimistic document open');

      // Set flag to prevent concurrent uploads
      uploadInProgressRef.current = true;

      canvasUploadDocumentFn(pendingUploadFile.file)
        .then(() => {
          console.log('✅ Document upload initiated successfully');
          setPendingUploadFile(null);
          uploadInProgressRef.current = false; // Reset flag
        })
        .catch((error) => {
          console.error('❌ Upload failed:', error);
          toast({
            title: "Upload failed",
            description: error instanceof Error ? error.message : "Failed to upload document",
            variant: "destructive"
          });
          setPendingUploadFile(null);
          uploadInProgressRef.current = false; // Reset flag even on error
        });
    }
  }, [pendingUploadFile, canvasUploadDocumentFn, toast]);

  // Handle pending YouTube video from deferred topic creation flow - Step 1: Detect and store
  useEffect(() => {
    const pendingVideo = location.state?.pendingYouTubeVideo;

    if (pendingVideo?.videoId && pendingVideo?.topicId && pendingVideo?.shouldSaveImmediately) {
      console.log('🎥 Detected pending YouTube video from router state');
      setPendingYouTubeVideo({
        videoId: pendingVideo.videoId,
        title: pendingVideo.title,
        embedUrl: pendingVideo.embedUrl,
        topicId: pendingVideo.topicId
      });

      // Clear router state immediately
      navigate(location.pathname + location.search, {
        replace: true,
        state: {}
      });
    }
  }, [location.state, navigate, location.pathname, location.search]);

  // Handle pending YouTube video from deferred topic creation flow - Step 2: Execute when canvas function is ready
  useEffect(() => {
    // Only proceed if we have pending video AND the canvas function is available
    if (pendingYouTubeVideo && canvasAddYouTubeVideoFn) {
      console.log('🎥 Canvas function available, adding YouTube video via canvas');

      // Call the canvas function which handles BOTH opening tab AND saving to DB
      canvasAddYouTubeVideoFn(
        pendingYouTubeVideo.videoId,
        pendingYouTubeVideo.title,
        pendingYouTubeVideo.embedUrl
      );

      // Don't show toast here - let handleVideoAdd show it after mutation completes
      // This ensures the Library updates before the user sees "success"

      // Clear pending state
      setPendingYouTubeVideo(null);
    }
  }, [pendingYouTubeVideo, canvasAddYouTubeVideoFn]);

  // Handle pending images from deferred topic creation flow - Step 1: Store in state
  useEffect(() => {
    const pendingImages = location.state?.pendingImages;

    if (pendingImages?.images && pendingImages?.topicId && pendingImages?.shouldAutoSave !== undefined) {
      console.log('🖼️ Detected pending images from router state');
      setPendingImages({
        images: pendingImages.images,
        topicId: pendingImages.topicId,
        shouldAutoSave: pendingImages.shouldAutoSave
      });

      // Clear router state immediately
      navigate(location.pathname + location.search, {
        replace: true,
        state: {}
      });
    }
  }, [location.state, navigate, location.pathname, location.search]);

  // Handle pending images from deferred topic creation flow - Step 2: Execute when canvas function is ready
  useEffect(() => {
    // Only proceed if we have pending images AND the canvas function is available
    if (pendingImages && canvasOpenImagesWithAutoSaveFn) {
      console.log('🖼️ Canvas function available, opening images with auto-save');

      // Call the canvas function - tab opens immediately, save happens in background
      canvasOpenImagesWithAutoSaveFn(pendingImages.images, pendingImages.shouldAutoSave);

      // Clear pending state
      setPendingImages(null);
    }
  }, [pendingImages, canvasOpenImagesWithAutoSaveFn]);

  // Handle topic not found - only redirect if there's an actual error and loading is complete
  useEffect(() => {
    if (topicError && !loading) {
      console.error('Topic not found:', topicError);
      navigate('/');
    }
  }, [topicError, loading, navigate]);

  // Stop voice agent when navigating between topics
  useEffect(() => {
    const currentTopicId = id;
    const previousTopicId = previousTopicIdRef.current;

    // If topic ID changed and we have a previous topic (not initial load)
    if (previousTopicId && currentTopicId !== previousTopicId) {
      // Check if voice agent is active
      const isAgentActive = geminiLive.agentStatus === 'connected' || geminiLive.agentStatus === 'connecting' || geminiLive.agentStatus === 'authenticating';
      if (isAgentActive) {
        console.log('🔄 Topic changed - stopping voice agent');
        geminiLive.handleStopConversation();
      }
    }

    // Update the ref for next comparison
    previousTopicIdRef.current = currentTopicId;
  }, [id, geminiLive.agentStatus, geminiLive.handleStopConversation]);

  // Session persistence trigger - batch save every 3 messages
  useEffect(() => {
    if (unsavedMessageCount >= 3) {
      console.log('📝 Triggering batch save - 3+ unsaved messages');
      persistConversationBatch();
    }
  }, [unsavedMessageCount, persistConversationBatch]);

  // Text message sending implementation
  const handleSendMessage = useCallback(async (messageText: string) => {
    if (!messageText.trim() || !id) return;
    try {
      // Create user message
      const userMessage = {
        id: `${Date.now()}_${messageSeqRef.current++}_user`,
        type: 'user' as const,
        content: messageText.trim(),
        timestamp: new Date()
      };

      // Add user message immediately
      appendMessage(userMessage);
      // If live protocol is enabled, send text to the live server
      try {
        (geminiLive as any)?.sendText?.(userMessage.content);
      } catch (e) {
        console.warn('Live sendText failed or unavailable', e);
      }
      console.log('📤 User message sent:', userMessage.content);

      // Fallback to text chat via backend SSE when not connected to voice WS
      const isConnectedToVoice = geminiLive.agentStatus === 'connected';
      if (!isConnectedToVoice) {
        // Add a placeholder agent message we will update as tokens arrive
        const agentMsgId = `${Date.now()}_${messageSeqRef.current++}_agent`;
        appendMessage({
          id: agentMsgId,
          type: 'agent',
          content: '',
          timestamp: new Date()
        });
        // Mark placeholder as streaming for UI (ChatThread reads message.streaming)
        setMessages(prev => prev.map(m => m.id === agentMsgId ? {
          ...m,
          streaming: true
        } : m));

        // Build request payload
        const {
          data: {
            session: authSession
          }
        } = await supabase.auth.getSession();
        const token = authSession?.access_token || '';
        const body = {
          message: userMessage.content,
          topic_id: id,
          topic_title: topic?.title || ''
          // Note: session_id removed - Agent Engine handles sessions automatically per topic
        } as const;
        try {
          const resp = await fetch(`${config.BACKEND_API_URL}/api/agent/stream_messages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'text/event-stream',
              ...(token ? {
                'Authorization': `Bearer ${token}`
              } : {})
            },
            body: JSON.stringify(body)
          });
          if (!resp.ok || !resp.body) {
            console.error('SSE response not ok or no body', resp.status, resp.statusText);
            // Replace placeholder with error
            setMessages(prev => prev.map(m => m.id === agentMsgId ? {
              ...m,
              content: 'Sorry, I could not get a reply. Please try again.'
            } : m));
            return;
          }
          const reader = resp.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let done = false;
          // Build cumulative content defensively: supports both delta and cumulative servers
          let assembledText = '';
          while (!done) {
            const {
              value,
              done: readerDone
            } = await reader.read();
            if (readerDone) break;
            buffer += decoder.decode(value, {
              stream: true
            });
            // Split on LF or CRLF double newlines
            const chunks = buffer.split(/\r?\n\r?\n/);
            buffer = chunks.pop() || '';
            for (const chunk of chunks) {
              const line = chunk.trim();
              if (!line.startsWith('data:')) continue;
              const jsonStr = line.slice(5).trim();
              let evt: any;
              try {
                evt = JSON.parse(jsonStr);
              } catch {
                continue;
              }
              if (evt.type === 'token' || typeof evt?.text === 'string' && !evt?.type) {
                const piece = typeof evt.text === 'string' ? evt.text : '';
                // If server sends cumulative text, prefer it; otherwise append deltas
                if (assembledText && piece.startsWith(assembledText)) {
                  assembledText = piece;
                } else if (piece && !assembledText.endsWith(piece)) {
                  assembledText = assembledText + piece;
                } else {
                  assembledText = piece || assembledText;
                }
                // Update placeholder with the growing content
                setMessages(prev => prev.map(m => m.id === agentMsgId ? {
                  ...m,
                  content: assembledText,
                  timestamp: new Date(),
                  streaming: true
                } : m));
              } else if (evt.type === 'done') {
                // Clear streaming flag on completion
                setMessages(prev => prev.map(m => m.id === agentMsgId ? {
                  ...m,
                  content: m.content && m.content.length > 0 ? m.content : assembledText || 'Sorry, I could not get a reply. Please try again.',
                  streaming: false
                } : m));
                done = true;
                break;
              } else if (evt.type === 'error') {
                setMessages(prev => prev.map(m => m.id === agentMsgId ? {
                  ...m,
                  content: `Sorry, something went wrong: ${evt.message || 'unknown error'}`,
                  streaming: false
                } : m));
                done = true;
                break;
              }
            }
          }
        } catch (err) {
          console.error('SSE fetch error', err);
          setMessages(prev => prev.map(m => m.id === agentMsgId ? {
            ...m,
            content: 'Network error while fetching reply.',
            streaming: false
          } : m));
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    }
  }, [id, appendMessage, toast, geminiLive.agentStatus, messageSeqRef, setMessages, session?.id, topic?.title]);
  const handleTabChange = (tab: 'chat' | 'notebook' | 'canvas') => {
    setActiveTab(tab);
  };

  // Note creation function - must be defined before useEffect that uses it
  const handleCreateNote = useCallback(async (title?: string) => {
    if (!id) return;
    try {
      const newNote = await createNoteMutation.mutateAsync({
        title: title || "New Note",
        content: [{
          type: "paragraph",
          children: [{
            text: ""
          }]
        }],
        topic_id: id
      });
      setActiveNote(newNote);
    } catch (error) {
      console.error("Error creating note:", error);
    }
  }, [id, createNoteMutation]);

  // Set the most recent note as active when notes load
  useEffect(() => {
    if (notes.length > 0 && !activeNote) {
      // Sort by updated_at to get the most recent note
      const sortedNotes = [...notes].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
      setActiveNote(sortedNotes[0]);
    }
  }, [notes, activeNote]);

  // Create first note if none exist
  useEffect(() => {
    if (!notesLoading && notes.length === 0 && id) {
      handleCreateNote("New Study Note");
    }
  }, [notesLoading, notes.length, id, handleCreateNote]);
  const handleNoteSelect = (note: StudyNote) => {
    setActiveNote(note);
  };
  const handleTitleChange = (newTitle: string) => {
    if (activeNote) {
      setActiveNote({
        ...activeNote,
        title: newTitle
      });
    }
  };

  // Document selection handler with study time tracking - similar to legacy Topic2Page.tsx:135-148
  const handleDocumentSelect = useCallback((documentId: string, documentName: string) => {
    // Update selected document for agent context
    setSelectedDocument(documentId);

    // Start tracking document study time
    studyTimeTracker.startActivity('document', {
      documentId
    });
    if (canvasDocumentOpenFn) {
      canvasDocumentOpenFn(documentId, documentName);
    } else {
      console.warn('Canvas document open function not available');
      // Fallback to navigation if canvas function not available
      navigate(`/document/${documentId}`);
    }
  }, [canvasDocumentOpenFn, studyTimeTracker, navigate]);

  // Video selection handler with study time tracking - similar to legacy Topic2Page.tsx:267-276
  const handleVideoSelect = useCallback((video: TopicVideo) => {
    // Start tracking video study time
    studyTimeTracker.startActivity('video', {
      videoId: video.id
    });
    if (canvasVideoOpenFn) {
      canvasVideoOpenFn(video);
    } else {
      console.warn('Canvas video open function not available');
    }
  }, [canvasVideoOpenFn, studyTimeTracker]);

  // Canvas integration handlers - similar to Topic2Page.tsx
  const handleCanvasDocumentOpenRef = useCallback((openFn: (documentId: string, documentName: string) => void) => {
    setCanvasDocumentOpenFn(() => openFn);
  }, []);
  const handleCanvasVideoOpenRef = useCallback((openFn: (video: TopicVideo) => void) => {
    setCanvasVideoOpenFn(() => openFn);
  }, []);
  const handleCanvasNoteOpenRef = useCallback((openFn: (note: StudyNote) => void) => {
    setCanvasNoteOpenFn(() => openFn);
  }, []);
  const handleCanvasUploadDocumentRef = useCallback((uploadFn: (file: File) => Promise<void>) => {
    setCanvasUploadDocumentFn(() => uploadFn);
  }, []);
  const handleCanvasAddYouTubeVideoRef = useCallback((addVideoFn: (videoId: string, title: string, embedUrl: string) => void) => {
    setCanvasAddYouTubeVideoFn(() => addVideoFn);
  }, []);
  const handleCanvasOpenImagesWithAutoSaveRef = useCallback((openImagesFn: (images: Array<{ id: string; file: File; blobUrl: string; name: string; }>, shouldSave?: boolean) => void) => {
    setCanvasOpenImagesWithAutoSaveFn(() => openImagesFn);
  }, []);
  const handleCanvasTabsChange = useCallback((hasContentTabs: boolean) => {
    setHasCanvasContent(hasContentTabs);
  }, []);

  // Vision integration handlers - similar to Topic2Page.tsx
  const handleCanvasViewportRef = useCallback((getCanvasElFn: () => HTMLElement | null) => {
    setGetCanvasEl(() => getCanvasElFn);
  }, []);
  const handleScreenStreamChange = useCallback((stream: MediaStream | null) => {
    setScreenStream(stream);
  }, []);
  const handleYouTubePlayerRef = useCallback((getPlayerRef: () => import('@/components/youtube/YouTubeEmbed').YouTubePlayerRef | null) => {
    setGetYouTubePlayer(() => getPlayerRef);
  }, []);
  const handleYouTubeVideoChange = useCallback((videoId: string | null) => {
    setCurrentYouTubeVideoId(videoId);
  }, []);

  // Screen share handler
  const handleScreenShare = useCallback(async () => {
    if (isScreenSharing) {
      // Stop screen sharing
      if (screenStream) {
        screenStream.getTracks().forEach(track => track.stop());
        setScreenStream(null);
      }
      setIsScreenSharing(false);
    } else {
      // Start screen sharing
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            mediaSource: 'screen'
          } as MediaTrackConstraints,
          audio: false
        });

        // Listen for user ending screen share via browser UI
        stream.getVideoTracks()[0].addEventListener('ended', () => {
          setIsScreenSharing(false);
          setScreenStream(null);
        });
        setScreenStream(stream);
        setIsScreenSharing(true);
      } catch (error) {
        console.error('Error starting screen share:', error);
        toast({
          title: "Screen Share Error",
          description: "Failed to start screen sharing. Please try again.",
          variant: "destructive"
        });
      }
    }
  }, [isScreenSharing, screenStream, toast]);

  // Column resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  }, []);
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    const container = document.querySelector('.chat-container');
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const mouseX = e.clientX - containerRect.left;
    const containerWidth = containerRect.width;
    const newColumn1Width = mouseX / containerWidth * 100;

    // Apply constraints: Column 1 min 25%, max 50% (which means Column 2 min 50%, max 75%)
    const constrainedWidth = Math.min(Math.max(newColumn1Width, 25), 50);
    setColumn1Width(constrainedWidth);
  }, [isResizing]);
  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add mouse event listeners
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Handle URL parameters for video adding and document uploads from landing page
  const hasProcessedUrlParams = useRef(false);
  useEffect(() => {
    const handleUrlParams = async () => {
      if (!topic || !user || loading || libraryLoading) return;
      if (hasProcessedUrlParams.current) return;
      const urlParams = new URLSearchParams(location.search);

      // Handle video adding
      const videoId = urlParams.get('addVideo');
      const videoTitle = urlParams.get('videoTitle');
      const embedUrl = urlParams.get('embedUrl');

      // Handle document upload
      const uploadDocument = urlParams.get('uploadDocument');
      const fileName = urlParams.get('fileName');
      const fileSize = urlParams.get('fileSize');
      const fileType = urlParams.get('fileType');
      const fileBlobUrl = urlParams.get('fileBlobUrl');
      if (!(videoId && videoTitle && embedUrl) && !(uploadDocument && fileName && fileBlobUrl)) return;

      // Early latch and clear URL parameters to prevent duplicate triggers
      hasProcessedUrlParams.current = true;
      window.history.replaceState({}, '', window.location.pathname);
      if (isTabletOrMobile) {
        setActiveTab('canvas');
      }

      // Handle video adding
      if (videoId && videoTitle && embedUrl) {
        console.log('🎥 Video add from URL params detected:', {
          videoId,
          videoTitle,
          canvasAddYouTubeVideoFn: !!canvasAddYouTubeVideoFn
        });

        // Wait for canvas function to be ready
        const waitForCanvasFn = async () => {
          const maxAttempts = 20;
          for (let i = 0; i < maxAttempts; i++) {
            if (canvasAddYouTubeVideoFn) {
              return true;
            }
            await new Promise(r => setTimeout(r, 200));
          }
          return false;
        };
        const ready = await waitForCanvasFn();
        if (!ready) {
          console.warn('⚠️ Canvas add video function not ready after waiting');
          return;
        }
        canvasAddYouTubeVideoFn(videoId, videoTitle, embedUrl);
      }

      // Handle document upload
      if (uploadDocument && fileName && fileBlobUrl) {
        console.log('📄 Document upload from URL params detected:', {
          fileName,
          canvasUploadDocumentFn: !!canvasUploadDocumentFn
        });

        // Wait for canvas function to be ready
        const waitForCanvasFn = async () => {
          const maxAttempts = 20;
          for (let i = 0; i < maxAttempts; i++) {
            if (canvasUploadDocumentFn) {
              return true;
            }
            await new Promise(r => setTimeout(r, 200));
          }
          return false;
        };
        const ready = await waitForCanvasFn();
        if (!ready) {
          console.warn('⚠️ Canvas upload function not ready after waiting');
          return;
        }

        // Recreate file from blob URL
        try {
          const response = await fetch(fileBlobUrl);
          const blob = await response.blob();
          const file = new File([blob], fileName, {
            type: fileType
          });
          await canvasUploadDocumentFn(file);

          // Clean up blob URL
          URL.revokeObjectURL(fileBlobUrl);
        } catch (error) {
          console.error('Error recreating file from blob URL:', error);
        }
      }
    };
    handleUrlParams();
  }, [topic, user, loading, libraryLoading, canvasAddYouTubeVideoFn, canvasUploadDocumentFn, isTabletOrMobile, location.search]);

  // Document upload handler - use canvas optimistic upload
  const handleUploadDocument = useCallback(async (file: File) => {
    setShowUploadModal(false);
    if (canvasUploadDocumentFn) {
      // Use the canvas's optimistic upload function
      await canvasUploadDocumentFn(file);
    } else {
      console.warn('Canvas upload function not available');
    }
  }, [canvasUploadDocumentFn]);
  const handleYouTubeVideoAdd = useCallback((videoId: string, title: string, embedUrl: string) => {
    if (canvasAddYouTubeVideoFn) {
      // Use the canvas's YouTube video add function which opens immediately
      canvasAddYouTubeVideoFn(videoId, title, embedUrl);
    } else {
      console.warn('Canvas add YouTube video function not available');
    }
  }, [canvasAddYouTubeVideoFn]);

  // Save video to topic database - called by CanvasSection
  const handleVideoAdd = useCallback(async (videoId: string, embedUrl: string) => {
    if (!topic?.id) return;
    try {
      await addTopicVideoMutation.mutateAsync({
        topicId: topic.id,
        videoId,
        embedUrl
      });

      // Trigger background YouTube analysis (fire-and-forget)
      triggerYouTubeAnalysis(videoId, topic.id).catch(console.warn);
      toast({
        title: "Video added",
        description: "YouTube video has been added to your topic."
      });
    } catch (error) {
      console.error('Failed to save video:', error);
      toast({
        title: "Error",
        description: "Failed to add video. Please try again.",
        variant: "destructive"
      });
    }
  }, [topic?.id, addTopicVideoMutation, toast]);

  // Document deletion handler - similar to legacy Topic2Page.tsx:278-300
  const confirmDeleteDocument = useCallback(async () => {
    if (!docToDelete || !user) return;
    try {
      setIsDeletingDoc(true);
      const {
        error
      } = await supabase.from('documents').delete().eq('id', docToDelete.id).eq('user_id', user.id);
      if (error) throw error;
      await queryClient.invalidateQueries({
        queryKey: ['topic-documents', id]
      });
      await queryClient.invalidateQueries({
        queryKey: ['topic-library-items', id]
      });
      setDeleteDialogOpen(false);
      setDocToDelete(null);
      toast({
        title: 'Document deleted',
        description: `${docToDelete.name} was removed.`
      });
    } catch (err: unknown) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('Delete document error:', error);
      toast({
        title: 'Failed to delete document',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsDeletingDoc(false);
    }
  }, [docToDelete, user, queryClient, id, toast]);

  // Video deletion handler - similar to legacy Topic2Page.tsx:302-317
  const confirmDeleteVideo = useCallback(async () => {
    if (!videoToDelete || !id) return;
    try {
      setIsDeletingVideo(true);
      await deleteVideoMutation.mutateAsync({
        id: videoToDelete.id,
        topicId: id
      });
      setDeleteVideoOpen(false);
      setVideoToDelete(null);
      toast({
        title: 'Video deleted',
        description: `${videoToDelete.name} was removed.`
      });
    } catch (err: unknown) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('Delete video error:', error);
      toast({
        title: 'Failed to delete video',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsDeletingVideo(false);
    }
  }, [videoToDelete, id, deleteVideoMutation, toast]);

  // Document delete trigger handler - sets up delete dialog
  const handleDocumentDelete = useCallback((documentId: string, documentName: string) => {
    setDocToDelete({
      id: documentId,
      name: documentName
    });
    setDeleteDialogOpen(true);
  }, []);

  // Video delete trigger handler - sets up delete dialog
  const handleVideoDelete = useCallback((videoId: string, videoName: string) => {
    setVideoToDelete({
      id: videoId,
      name: videoName
    });
    setDeleteVideoOpen(true);
  }, []);

  // Handle chat column visibility toggle
  const handleToggleChatColumn = useCallback(() => {
    setIsChatColumnVisible(prev => !prev);
  }, []);

  // Handle notebook column visibility toggle
  const handleToggleNotebookColumn = useCallback(() => {
    setIsNotebookColumnVisible(prev => !prev);
  }, []);
  if (loading) {
    return <div className="h-screen bg-[#F8F8F6] flex flex-col overflow-hidden">
      <div className="flex items-center justify-center h-full">
        <div className="text-[#171717]" style={{
          fontFamily: 'Lexend Deca'
        }}>Loading topic...</div>
      </div>
    </div>;
  }
  if (!topic) {
    return <div className="h-screen bg-[#F8F8F6] flex flex-col overflow-hidden">
      <div className="flex items-center justify-center h-full">
        <div className="text-[#171717]" style={{
          fontFamily: 'Lexend Deca'
        }}>Topic not found</div>
      </div>
    </div>;
  }
  return <AppLayout pageTitle={topic.title}>
    <div className="h-full bg-[#F8F8F6] flex flex-col chat-container" style={{
      fontFamily: 'Lexend Deca'
    }}>
      <div className="flex-1 min-h-0 flex">
        {isTabletOrMobile ?
          // Mobile/Tablet Layout: Single column with 3 tabs
          <div className="w-full flex flex-col h-full">
            {/* Row 1 - Top */}
            <div className="flex-shrink-0">
              <ChatTabs activeTab={activeTab} onTabChange={handleTabChange} showCanvasTab={true} />
            </div>

            {/* Row 2 - Content */}
            <div className="flex-1 min-h-0">
              {activeTab === 'chat' && <div className="h-full max-w-4xl mx-auto">
                <ChatThread onSendMessage={handleSendMessage} transcript={transcriptMessages} sessionName={session?.title || topic?.title || "Chat"} topicId={id} userName={userProfile.full_name} />
              </div>}

              {activeTab === 'notebook' && <div className="h-full max-w-4xl mx-auto px-4 pt-4">
                {activeNote ? <div className="h-full flex flex-col">
                  {/* Combined Title + Dropdown Header */}
                  <div className="flex-shrink-0 border-b border-gray-200 p-3">
                    <div className="flex items-center gap-2">
                      <input type="text" value={activeNote.title || ""} onChange={e => handleTitleChange(e.target.value)} className="text-xl font-semibold text-[#171717] bg-transparent border-none outline-none flex-1" placeholder="Enter note title..." style={{
                        fontFamily: 'Lexend Deca'
                      }} />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          {notes.map(note => <DropdownMenuItem key={note.id} onClick={() => handleNoteSelect(note)} className={`flex items-center justify-between ${activeNote?.id === note.id ? "bg-blue-50" : ""}`}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <FileText className="w-3 h-3 text-gray-500 flex-shrink-0" />
                              <span className="text-sm font-medium text-gray-700 truncate">
                                {note.title || "Untitled"}
                              </span>
                            </div>
                          </DropdownMenuItem>)}
                          <DropdownMenuItem onClick={() => handleCreateNote()} className="border-t border-gray-100 mt-1 pt-1">
                            <Plus className="w-3 h-3 mr-2" />
                            Add New Note
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Note Editor */}
                  <div className="flex-1 min-h-0">
                    <StudyNoteEditor key={activeNote.id} noteId={activeNote.id} topicId={id!} initialContent={activeNote.content?.length > 0 ? activeNote.content : [{
                      type: "paragraph",
                      children: [{
                        text: ""
                      }]
                    }]} initialTitle={activeNote.title} onTitleChange={handleTitleChange} editorRef={editorRef} />
                  </div>
                </div> : <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Loading notes...</p>
                  </div>
                </div>}
              </div>}

              {activeTab === 'canvas' && <div className="h-full pt-6 px-4">
                <CanvasSection openUploadOnMount={shouldOpenUploadModal} openYouTubeOnMount={shouldOpenYouTubeModal} onDocumentOpen={handleCanvasDocumentOpenRef} onVideoOpen={handleCanvasVideoOpenRef} onNoteOpen={handleCanvasNoteOpenRef} onUploadDocument={handleCanvasUploadDocumentRef} onAddYouTubeVideo={handleCanvasAddYouTubeVideoRef} onOpenImagesWithAutoSaveRef={handleCanvasOpenImagesWithAutoSaveRef} onVideoAdd={handleVideoAdd} onTabsChange={handleCanvasTabsChange} onCanvasViewportRef={handleCanvasViewportRef} onScreenStreamChange={handleScreenStreamChange} onYouTubePlayerRef={handleYouTubePlayerRef} onYouTubeVideoChange={handleYouTubeVideoChange} onSlideContextChange={setActiveSlideContext} documents={documents} videos={videos} libraryItems={libraryItems} onDocumentSelect={handleDocumentSelect} onVideoSelect={handleVideoSelect} onDocumentDelete={handleDocumentDelete} onVideoDelete={handleVideoDelete} />
              </div>}
            </div>
          </div> :
          // Desktop Layout: Three columns (chat, canvas, notebook)
          <>
            {/* Chat Column - Leftmost */}
            <div className="transition-all duration-300 ease-in-out flex flex-col min-h-0" style={{
              width: isChatColumnVisible ? `${chatColumnWidth}%` : '0%',
              opacity: isChatColumnVisible ? 1 : 0,
              overflow: isChatColumnVisible ? 'visible' : 'hidden',
              marginLeft: '1rem'
            }}>
              <div className="h-full flex flex-col pt-6">
                <ChatThread onSendMessage={handleSendMessage} transcript={transcriptMessages} sessionName={session?.title || topic?.title || "Chat"} topicId={id} userName={userProfile.full_name} />
              </div>
            </div>

            {/* Resize Handle - Left (between chat and canvas) */}
            {isChatColumnVisible && hasCanvasContent && <div className="w-px bg-border cursor-col-resize hover:bg-gray-400 transition-colors" onMouseDown={handleMouseDown} />}

            {/* Canvas Column - Middle (always render for function exposure) */}
            <div className="flex-1 min-h-0 flex flex-col transition-all duration-300" style={{
              marginLeft: isChatColumnVisible ? '0.5rem' : '1rem',
              marginRight: isNotebookColumnVisible ? '0.5rem' : '1rem'
            }}>
              <CanvasSection openUploadOnMount={shouldOpenUploadModal} openYouTubeOnMount={shouldOpenYouTubeModal} onDocumentOpen={handleCanvasDocumentOpenRef} onVideoOpen={handleCanvasVideoOpenRef} onNoteOpen={handleCanvasNoteOpenRef} onUploadDocument={handleCanvasUploadDocumentRef} onAddYouTubeVideo={handleCanvasAddYouTubeVideoRef} onOpenImagesWithAutoSaveRef={handleCanvasOpenImagesWithAutoSaveRef} onVideoAdd={handleVideoAdd} onTabsChange={handleCanvasTabsChange} onCanvasViewportRef={handleCanvasViewportRef} onScreenStreamChange={handleScreenStreamChange} onYouTubePlayerRef={handleYouTubePlayerRef} onYouTubeVideoChange={handleYouTubeVideoChange} onSlideContextChange={setActiveSlideContext} documents={documents} videos={videos} libraryItems={libraryItems} onDocumentSelect={handleDocumentSelect} onVideoSelect={handleVideoSelect} onDocumentDelete={handleDocumentDelete} onVideoDelete={handleVideoDelete} />
            </div>

            {/* Resize Handle - Right (between canvas and notebook) */}
            {isNotebookColumnVisible && hasCanvasContent && <div className="w-px bg-border cursor-col-resize hover:bg-gray-400 transition-colors" onMouseDown={handleMouseDown} />}

            {/* Notebook Column - Rightmost */}
            <div className="transition-all duration-300 ease-in-out flex flex-col min-h-0" style={{
              width: isNotebookColumnVisible ? `${notebookColumnWidth}%` : '0%',
              opacity: isNotebookColumnVisible ? 1 : 0,
              overflow: isNotebookColumnVisible ? 'visible' : 'hidden',
              marginRight: '1rem'
            }}>
              <div className="h-full flex flex-col pt-2">
                {activeNote ? <div className="h-full flex flex-col">
                  {/* Title + Dropdown Header */}
                  <div className="flex-shrink-0 border-b border-gray-200 p-2">
                    <div className="flex items-center gap-2">
                      <input type="text" value={activeNote.title || ""} onChange={e => handleTitleChange(e.target.value)} className="text-lg font-semibold text-[#171717] bg-transparent border-none outline-none flex-1" placeholder="Enter note title..." style={{
                        fontFamily: 'Lexend Deca'
                      }} />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          {notes.map(note => <DropdownMenuItem key={note.id} onClick={() => handleNoteSelect(note)} className={`flex items-center justify-between ${activeNote?.id === note.id ? "bg-blue-50" : ""}`}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <FileText className="w-3 h-3 text-gray-500 flex-shrink-0" />
                              <span className="text-sm font-medium text-gray-700 truncate">
                                {note.title || "Untitled"}
                              </span>
                            </div>
                          </DropdownMenuItem>)}
                          <DropdownMenuItem onClick={() => handleCreateNote()} className="border-t border-gray-100 mt-1 pt-1">
                            <Plus className="w-3 h-3 mr-2" />
                            Add New Note
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Note Editor */}
                  <div className="flex-1 min-h-0">
                    <StudyNoteEditor key={activeNote.id} noteId={activeNote.id} topicId={id!} initialContent={activeNote.content?.length > 0 ? activeNote.content : [{
                      type: "paragraph",
                      children: [{
                        text: ""
                      }]
                    }]} initialTitle={activeNote.title} onTitleChange={handleTitleChange} editorRef={editorRef} />
                  </div>
                </div> : <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Loading notes...</p>
                  </div>
                </div>}
              </div>
            </div>
          </>}
      </div>

      {/* Dock - Always visible at bottom (desktop and mobile) with high z-index */}
      <div className="flex-shrink-0 w-full px-4 py-1 pb-[calc(0.25rem+env(safe-area-inset-bottom))] bg-[#FAFAFA] border-t border-[#E5E5E5] relative z-[10001]">
        <AgentDock agentStatus={geminiLive.agentStatus} isUserMuted={geminiLive.isUserMuted} isPushToTalkLocked={geminiLive.isPushToTalkLocked} sessionDuration={geminiLive.sessionDuration} isAgentActive={geminiLive.isAgentActive} isAgentTyping={geminiLive.isAgentTyping} isUserSpeaking={isUserSpeaking} onConnectAgent={geminiLive.handleConnectAgent} onPushToTalkStart={geminiLive.handlePushToTalkStart} onPushToTalkEnd={geminiLive.handlePushToTalkEnd} onToggleLock={geminiLive.handleToggleLock} onStopConversation={geminiLive.handleStopConversation} floating={false} onSendMessage={handleSendMessage} chatInputText={chatInputText} onChatInputTextChange={setChatInputText} showChatInput={inputMode === 'chat'} hasMessages={transcriptMessages.length > 0} onShowChatInput={() => setInputMode('chat')} onHideChatInput={() => setInputMode('talk')} isChatColumnVisible={isChatColumnVisible} isNotebookColumnVisible={isNotebookColumnVisible} onToggleChatColumn={handleToggleChatColumn} onToggleNotebookColumn={handleToggleNotebookColumn} showColumnToggles={!isTabletOrMobile && hasCanvasContent} />
      </div>

      {/* Upload Document Modal */}
      {showUploadModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Upload Document</h3>
            <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <DragDropUpload onUpload={handleUploadDocument} isUploading={false} />

          <div className="mt-4 text-xs text-slate-500 text-center">
            Upload PDF documents to add them to your topic library
          </div>
        </div>
      </div>}

      {/* YouTube URL Modal */}
      <YouTubeUrlModal isOpen={showYouTubeModal} onClose={() => setShowYouTubeModal(false)} onVideoAdd={handleYouTubeVideoAdd} />

      {/* Floating Agent Dock - Shows when agent is connected and user leaves chat tab */}
      {activeTab !== 'chat' && geminiLive.agentStatus === 'connected' && <AgentDock agentStatus={geminiLive.agentStatus} isUserMuted={geminiLive.isUserMuted} isPushToTalkLocked={geminiLive.isPushToTalkLocked} sessionDuration={geminiLive.sessionDuration} isAgentActive={geminiLive.isAgentActive} isAgentTyping={geminiLive.isAgentTyping} isUserSpeaking={isUserSpeaking} onConnectAgent={geminiLive.handleConnectAgent} onPushToTalkStart={geminiLive.handlePushToTalkStart} onPushToTalkEnd={geminiLive.handlePushToTalkEnd} onToggleLock={geminiLive.handleToggleLock} onStopConversation={geminiLive.handleStopConversation} floating={true} column1Width={column1Width} hasCanvasContent={hasCanvasContent} isChatColumnVisible={isChatColumnVisible} isNotebookColumnVisible={isNotebookColumnVisible} onToggleChatColumn={handleToggleChatColumn} onToggleNotebookColumn={handleToggleNotebookColumn} showColumnToggles={!isTabletOrMobile && hasCanvasContent} />}

      {/* Removed session confirmation dialog - single session per topic */}

      {/* Document Delete Dialog - similar to legacy Topic2Page.tsx:645-652 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{docToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingDoc}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteDocument} disabled={isDeletingDoc}>
              {isDeletingDoc ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Video Delete Dialog - similar to legacy Topic2Page.tsx:655-670 */}
      <AlertDialog open={deleteVideoOpen} onOpenChange={setDeleteVideoOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Video</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{videoToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingVideo}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteVideo} disabled={isDeletingVideo}>
              {isDeletingVideo ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  </AppLayout>;
};
export default ChatPage;